import {
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  Tooltip,
  Typography,
  makeStyles,
} from '@material-ui/core';
import * as React from 'react';
import { ExternalOrderService } from 'services';
import { SnackbarContext } from 'components';
import RefreshIcon from '@material-ui/icons/Refresh';
import ScheduleIcon from '@material-ui/icons/Schedule';
import { WebsiteHoursDialog } from './WebsiteHoursDialog';

/**
 * Website-ordering ribbon for OUR OWN storefront at pekingmalta.com/order.
 *
 * Since 2026-09-05 the ordering hours open and close the site by themselves,
 * so a forgotten switch cannot leave it taking orders at 3am. The buttons here
 * are a PAUSE on top of that: pausing stops everything (order-ahead included)
 * and holds until someone resumes, which is why a paused site shouts about it
 * in red rather than sitting quietly in a chip.
 */
const useStyles = makeStyles((theme) => ({
  bar: {
    padding: theme.spacing(1, 2),
    marginBottom: theme.spacing(2),
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    borderLeft: '4px solid #6a1b9a', // Website purple, matches the alert popup
  },
  barPaused: {
    background: '#ffebee',
    borderLeft: '4px solid #b71c1c',
  },
  statusBlock: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  chipOnline: {
    background: '#1b5e20',
    color: 'white',
    fontWeight: 700,
    fontSize: '0.95rem',
    height: 28,
  },
  chipClosed: {
    background: '#455a64',
    color: 'white',
    fontWeight: 700,
    fontSize: '0.95rem',
    height: 28,
  },
  chipPaused: {
    background: '#b71c1c',
    color: 'white',
    fontWeight: 700,
    fontSize: '0.95rem',
    height: 28,
  },
  chipUnknown: {
    background: '#757575',
    color: 'white',
    fontWeight: 700,
    fontSize: '0.95rem',
    height: 28,
  },
  meta: {
    color: theme.palette.text.secondary,
    fontSize: '0.8rem',
  },
  pausedNote: {
    color: '#b71c1c',
    fontWeight: 700,
    fontSize: '0.85rem',
  },
  actions: {
    display: 'flex',
    gap: theme.spacing(1),
    flexWrap: 'wrap',
    marginLeft: 'auto',
  },
}));

interface VenueState {
  isOnline: boolean | null;
  isPaused: boolean;
  openNow: boolean;
  closesAt: string | null;
  nextOpen: { date: string; from: string; to: string } | null;
}

const dayLabel = (isoDate: string): string => {
  const today = new Date();
  const toKey = (d: Date) =>
    `${d.getFullYear()}-${`${d.getMonth() + 1}`.padStart(2, '0')}-${`${d.getDate()}`.padStart(2, '0')}`;
  const tomorrow = new Date(today.getTime() + 24 * 3600 * 1000);
  if (isoDate === toKey(today)) return 'today';
  if (isoDate === toKey(tomorrow)) return 'tomorrow';
  const parts = isoDate.split('-').map(Number);
  return new Date(parts[0], parts[1] - 1, parts[2]).toLocaleDateString(
    undefined,
    { weekday: 'long' },
  );
};

export const WebsiteProviderStatusBar: React.FC<{ compact?: boolean }> = ({
  compact,
}) => {
  const classes = useStyles();
  const snackbar = React.useContext(SnackbarContext);
  const [state, setState] = React.useState<VenueState | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [pending, setPending] = React.useState<'ONLINE' | 'OFFLINE' | null>(
    null,
  );
  const [hoursOpen, setHoursOpen] = React.useState(false);

  const refresh = React.useCallback(() => {
    setLoading(true);
    ExternalOrderService.websiteGetVenueStatus()
      .then((r: any) => {
        const s = r?.status;
        if (!s) {
          setState(null);
          return;
        }
        setState({
          isOnline: typeof s.isOnline === 'boolean' ? s.isOnline : null,
          isPaused: !!s.isPaused,
          openNow: !!s.openNow,
          closesAt: s.closesAt || null,
          nextOpen: s.nextOpen || null,
        });
      })
      .catch(() => setState(null))
      .finally(() => setLoading(false));
  }, []);

  React.useEffect(() => {
    refresh();
  }, [refresh]);

  // The hours move the site on their own, so a bar left open on a screen all
  // evening would otherwise still claim ONLINE after closing time.
  React.useEffect(() => {
    const timer = setInterval(refresh, 60000);
    return () => clearInterval(timer);
  }, [refresh]);

  const setStatus = async (target: 'ONLINE' | 'OFFLINE') => {
    setPending(target);
    try {
      await ExternalOrderService.websiteSetVenueOnline({ status: target });
      snackbar.open(
        target === 'ONLINE'
          ? 'Website ordering resumed - the opening hours are back in charge'
          : 'Website ordering PAUSED - no new orders until you resume',
      );
      refresh();
    } catch (e) {
      snackbar.error(e);
    } finally {
      setPending(null);
    }
  };

  const paused = state?.isPaused === true;
  const openNow = state?.openNow === true;

  const statusChip =
    state === null ? (
      <Chip label="UNKNOWN" className={classes.chipUnknown} />
    ) : paused ? (
      <Chip label="PAUSED" className={classes.chipPaused} />
    ) : openNow ? (
      <Chip label="TAKING ORDERS" className={classes.chipOnline} />
    ) : (
      <Chip label="CLOSED" className={classes.chipClosed} />
    );

  const line = () => {
    if (state === null) return '';
    if (paused) {
      return 'Paused by staff. Customers cannot order at all, not even ahead. This stays until you resume it.';
    }
    if (openNow) {
      return state.closesAt
        ? `Open automatically until ${state.closesAt}.`
        : 'Open automatically.';
    }
    if (state.nextOpen) {
      return `Closed now - opens by itself ${dayLabel(state.nextOpen.date)} at ${state.nextOpen.from}. Order-ahead is still accepted.`;
    }
    return 'Closed - no ordering hours set.';
  };

  return (
    <>
      <Paper
        className={`${classes.bar} ${paused ? classes.barPaused : ''}`}
        elevation={compact ? 0 : 1}
        style={compact ? { margin: 0, flex: 1, minWidth: 280 } : undefined}
      >
        <div className={classes.statusBlock}>
          <Typography variant="subtitle2" style={{ marginRight: 4 }}>
            Website ordering:
          </Typography>
          {statusChip}
          <Tooltip title="Refresh website ordering status">
            <IconButton size="small" onClick={refresh} disabled={loading}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <span className={paused ? classes.pausedNote : classes.meta}>
            {loading && state === null ? 'loading…' : line()}
          </span>
        </div>

        <div className={classes.actions}>
          {!compact && (
            <Button
              variant="outlined"
              size="small"
              startIcon={<ScheduleIcon />}
              onClick={() => setHoursOpen(true)}
            >
              Ordering hours
            </Button>
          )}
          {paused ? (
            <Button
              variant="contained"
              size="small"
              style={{ background: '#1b5e20', color: '#fff' }}
              disabled={pending !== null}
              onClick={() => setStatus('ONLINE')}
              startIcon={
                pending === 'ONLINE' ? (
                  <CircularProgress size={16} color="inherit" />
                ) : undefined
              }
            >
              Resume orders
            </Button>
          ) : (
            <Button
              variant="contained"
              color="secondary"
              size="small"
              disabled={pending !== null}
              onClick={() => setStatus('OFFLINE')}
              startIcon={
                pending === 'OFFLINE' ? (
                  <CircularProgress size={16} color="inherit" />
                ) : undefined
              }
            >
              Pause orders
            </Button>
          )}
        </div>
      </Paper>

      <WebsiteHoursDialog
        open={hoursOpen}
        onClose={() => setHoursOpen(false)}
        onSaved={refresh}
      />
    </>
  );
};
